import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PCLayout from './PCLayout';
import { Accolades, Projects, Blog } from './Misc';

const PCMiscView = ({ isDarkMode, toggleTheme }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine initial active section based on location state or hash
    const getInitialSection = () => {
        if (location.state && location.state.targetSection) {
            return location.state.targetSection.toLowerCase();
        }
        if (location.hash) {
            const hashSection = location.hash.substring(1);
            if (['accolades', 'projects', 'blog'].includes(hashSection)) {
                return hashSection;
            }
        }
        return 'accolades';
    };

    // State to track active section
    const [activeSection, setActiveSection] = useState(getInitialSection());

    // Flag to prevent unnecessary updates when programmatically scrolling
    const isScrollingProgrammatically = useRef(false);

    // Cooldown timer to prevent active indicator flickering
    const cooldownActiveRef = useRef(false);
    const cooldownTimerRef = useRef(null);

    // Container ref for scroll observation
    const contentMainRef = useRef(null);

    // Refs for each section
    const accoladesRef = useRef(null);
    const projectsRef = useRef(null);
    const blogRef = useRef(null);

    // Set cooldown for the active indicator
    const setCooldown = () => {
        // Set cooldown active
        cooldownActiveRef.current = true;

        // Clear any existing cooldown timer
        if (cooldownTimerRef.current) {
            clearTimeout(cooldownTimerRef.current);
        }

        // Set new cooldown timer
        cooldownTimerRef.current = setTimeout(() => {
            cooldownActiveRef.current = false;
        }, 500); // 0.5 second cooldown
    };

    // Handle menu item click
    const handleMenuItemClick = (sectionId) => {
        const aboutMeSections = ['me', 'education', 'hobbies'];
        const professionalSections = ['experience', 'research', 'skills', 'more'];

        if (aboutMeSections.includes(sectionId.toLowerCase())) {
            // Navigate to main page with state containing target section
            navigate('/', { state: { targetSection: sectionId.toLowerCase() } });
            return;
        }

        if (professionalSections.includes(sectionId.toLowerCase())) {
            // Navigate to professional page with state containing target section
            navigate('/prof', { state: { targetSection: sectionId.toLowerCase() } });
            return;
        }

        const sectionRefs = {
            'accolades': accoladesRef,
            'projects': projectsRef,
            'blog': blogRef
        };

        const ref = sectionRefs[sectionId.toLowerCase()];
        if (ref && ref.current) {
            // Set flag to prevent change from triggering another scroll
            isScrollingProgrammatically.current = true;

            // Set the active section immediately
            setActiveSection(sectionId.toLowerCase());

            // Activate cooldown to prevent flickering
            setCooldown();

            // Scroll to section
            ref.current.scrollIntoView({ behavior: 'smooth' });

            // Reset flag after animation completes
            setTimeout(() => {
                isScrollingProgrammatically.current = false;
            }, 1000);
        }
    };

    // Initialize the Misc page by selecting a section
    useEffect(() => {
        // Check if we arrived from another page with a target section
        let initialSection = 'accolades'; // Default

        if (location.state && location.state.targetSection) {
            // Get target section from location state
            const requestedSection = location.state.targetSection;
            if (['accolades', 'projects', 'blog'].includes(requestedSection)) {
                initialSection = requestedSection;
            }
        }
        // Support for hash in URL as fallback for direct links
        else if (location.hash) {
            const hashSection = location.hash.substring(1);
            if (['accolades', 'projects', 'blog'].includes(hashSection)) {
                initialSection = hashSection;
            }
        }

        // Set initial active section (redundant but safe)
        setActiveSection(initialSection);

        // Scroll to the section if we have a ref for it
        const sectionRefs = {
            'accolades': accoladesRef,
            'projects': projectsRef,
            'blog': blogRef
        };

        const ref = sectionRefs[initialSection];
        if (ref && ref.current) {
            // Set cooldown immediately to prevent observer from overriding active section
            setCooldown();

            // Wait for DOM to fully render
            setTimeout(() => {
                ref.current.scrollIntoView({ behavior: 'smooth' });
                setCooldown(); // Prevent flickering
            }, 100);
        }
    }, [location]);

    // Setup intersection observer to detect which section is visible
    useEffect(() => {
        if (!contentMainRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                // Skip intersection updates if we're in cooldown period
                if (cooldownActiveRef.current) return;

                entries.forEach(entry => {
                    if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                        const sectionId = entry.target.id;

                        // Update active section state only if not in cooldown period
                        setActiveSection(sectionId);
                    }
                });
            },
            {
                root: contentMainRef.current,
                rootMargin: '0px',
                threshold: 0.6, // Section is considered "active" when 60% visible
            }
        );

        // Observe each section
        const sections = [
            accoladesRef.current,
            projectsRef.current,
            blogRef.current
        ].filter(Boolean);

        sections.forEach(section => {
            if (section) observer.observe(section);
        });

        return () => {
            sections.forEach(section => {
                if (section) observer.unobserve(section);
            });

            // Clear any lingering cooldown timer
            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current);
            }
        };
    }, []);

    return (
        <PCLayout
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            activePage={activeSection}
            contentRef={contentMainRef}
            onMenuItemClick={handleMenuItemClick}
        >
            {/* Misc Section components */}
            <div id="accolades" ref={accoladesRef}>
                <Accolades isDarkMode={isDarkMode} />
            </div>

            <div id="projects" ref={projectsRef}>
                <Projects isDarkMode={isDarkMode} />
            </div>

            <div id="blog" ref={blogRef}>
                <Blog isDarkMode={isDarkMode} />
            </div>
        </PCLayout>
    );
};

export default PCMiscView;